/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';
import React, { useContext } from 'react';
import type { RenderFn } from '@atlassian/forge-ui-types';
import { LinkButton as PlatformLinkButton } from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';

import { sanitizeUrl } from '../../../utils';
import { useAdaptEventHandler, adaptEventHandler } from '../../utils';

import { useIsBylineItem } from '../../utils/useIsModule';
import { fg } from '@atlaskit/platform-feature-flags';

import type { LinkButtonProps as PlatformLinkButtonProps } from '@atlaskit/button/new';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { RendererContext } from '../../../context';
import { useNavigation } from '../../../custom-ui/bridge/methods/useNavigation';

const defaultStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	span: { color: token('color.text', '#42526E') },
});
const primaryStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	span: { color: token('color.text.inverse') },
});
const warningStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	span: { color: token('color.text.warning.inverse', '#172B4D') },
});
const dangerStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	span: { color: token('color.text.inverse') },
});
const discoveryStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	span: { color: token('color.text.inverse') },
});
const subtleStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	span: { color: token('color.text', '#42526E') },
});

/**
 * Type for link button had to be defined here because the prop `href` should only take strings here
 * and not custom RouterLinkConfigs
 */
export type LinkButtonProps = Pick<
	PlatformLinkButtonProps,
	| 'children'
	| 'target'
	| 'autoFocus'
	| 'isDisabled'
	| 'isSelected'
	| 'onBlur'
	| 'onClick'
	| 'onFocus'
	| 'testId'
	| 'shouldFitContainer'
> & {
	appearance?: PlatformLinkButtonProps['appearance'] | 'link' | 'subtle-link';
	href?: string;
	ref?: React.Ref<HTMLAnchorElement>;
	spacing?: PlatformLinkButtonProps['spacing'] | 'none';
};

/**
 * Mapping the deprecated 'link' and 'subtle-link' appearances to 'subtle'
 */
const mapDeprecatedAppearances = (appearance: LinkButtonProps['appearance']) =>
	appearance === 'link' || appearance === 'subtle-link' ? 'subtle' : appearance;

/**
 * Mapping the deprecated 'none' spacing to 'compact'
 */
const mapDeprecatedSpacing = (spacing: LinkButtonProps['spacing']) =>
	spacing === 'none' ? 'compact' : spacing;

const BaseLinkButton = (props: Parameters<RenderFn>[0]) => {
	const {
		target,
		href,
		autoFocus,
		isDisabled,
		isSelected,
		onBlur,
		onClick,
		onFocus,
		spacing,
		testId,
		shouldFitContainer,
		appearance,
	} = props.forgeDoc.props as LinkButtonProps;

	const { egress, properties } = useContext(RendererContext);
	const extension = { egress, properties };
	const { onNavigate, getModalJsx } = useNavigation({ extension });

	const openNewTab = target === '_blank';
	const santizedUrl = sanitizeUrl(href);

	const onClickWithSafeNavigation = (
		event: React.MouseEvent<HTMLAnchorElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		onClick?.(event, analyticsEvent);
		if (href) {
			onNavigate({ type: openNewTab ? 'new-tab' : 'same-tab', url: santizedUrl });
		}
	};

	return (
		<React.Fragment>
			{getModalJsx()}
			<PlatformLinkButton
				target={target}
				// safety measure
				href={santizedUrl}
				// safety measure
				rel={'noopener noreferrer'}
				children={props.forgeDoc.children.map((c) => (
					<React.Fragment key={c.key}>{props.render(c)}</React.Fragment>
				))}
				autoFocus={autoFocus}
				isDisabled={isDisabled}
				isSelected={isSelected}
				onBlur={adaptEventHandler(onBlur)}
				onClick={(e, ...args) => {
					// stop the default navigation behaviour from anchor tags
					e.preventDefault();
					adaptEventHandler?.(onClickWithSafeNavigation)?.(e, ...args);
				}}
				onFocus={adaptEventHandler(onFocus)}
				spacing={mapDeprecatedSpacing(spacing)}
				testId={testId}
				shouldFitContainer={shouldFitContainer}
				appearance={mapDeprecatedAppearances(appearance)}
			/>
		</React.Fragment>
	);
};

const BaseLinkButtonWithAnalytics = (props: Parameters<RenderFn>[0]) => {
	const {
		target,
		href,
		autoFocus,
		isDisabled,
		isSelected,
		onBlur,
		onClick,
		onFocus,
		spacing,
		testId,
		shouldFitContainer,
		appearance,
	} = props.forgeDoc.props as LinkButtonProps;

	const adaptEventHandler = useAdaptEventHandler();

	const { egress, properties } = useContext(RendererContext);
	const extension = { egress, properties };
	const { onNavigate, getModalJsx } = useNavigation({ extension });

	const openNewTab = target === '_blank';
	const santizedUrl = sanitizeUrl(href);

	const onClickWithSafeNavigation = (
		event: React.MouseEvent<HTMLAnchorElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		onClick?.(event, analyticsEvent);
		if (href) {
			onNavigate({ type: openNewTab ? 'new-tab' : 'same-tab', url: santizedUrl });
		}
	};

	return (
		<React.Fragment>
			{getModalJsx()}
			<PlatformLinkButton
				target={target}
				// safety measure
				href={santizedUrl}
				// safety measure
				rel={'noopener noreferrer'}
				children={props.forgeDoc.children.map((c) => (
					<React.Fragment key={c.key}>{props.render(c)}</React.Fragment>
				))}
				autoFocus={autoFocus}
				isDisabled={isDisabled}
				isSelected={isSelected}
				onBlur={adaptEventHandler(onBlur)}
				onClick={(e, ...args) => {
					// stop the default navigation behaviour from anchor tags
					e.preventDefault();
					adaptEventHandler?.(onClickWithSafeNavigation)?.(e, ...args);
				}}
				onFocus={adaptEventHandler(onFocus)}
				spacing={mapDeprecatedSpacing(spacing)}
				testId={testId}
				shouldFitContainer={shouldFitContainer}
				appearance={mapDeprecatedAppearances(appearance)}
			/>
		</React.Fragment>
	);
};

const Component = (props: Parameters<RenderFn>[0]) => {
	if (fg('forge-ui-performance-analytics-provider')) {
		return <BaseLinkButtonWithAnalytics {...props} />;
	}
	return <BaseLinkButton {...props} />;
};

/**
 * Renders a link in the style of a button.
 */
export const LinkButton = (props: Parameters<RenderFn>[0]): JSX.Element => {
	const isBylineItem = useIsBylineItem();

	const { appearance } = props.forgeDoc.props as LinkButtonProps;

	// This overrides global anchor tag styling that affects link button in byline content apps
	return isBylineItem ? (
		<div
			css={[
				appearance === undefined && defaultStyle,
				appearance === 'primary' && primaryStyle,
				appearance === 'warning' && warningStyle,
				appearance === 'danger' && dangerStyle,
				appearance === 'discovery' && discoveryStyle,
				appearance === 'subtle' && subtleStyle,
			]}
		>
			<Component {...props} />
		</div>
	) : (
		<Component {...props} />
	);
};
